import React, { useState, useEffect, useRef } from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    IconButton,
    Typography,
    Box,
    CircularProgress,
    Tooltip,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useTranslation } from "react-i18next";
import { getUserById, createUserOpt, deleteUserOpt } from "utils/Users";

interface EnableOTPProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    setAlert: (alert: { msg: string; severity: string }) => void;
    setOpenAlert: (open: boolean) => void;
    row: { id: number; username: string } | undefined;
    reloading: () => void;
    setSelectedRow: (rows: any[]) => void;
}

const EnableOTP = ({
    open,
    setOpen,
    setAlert,
    setOpenAlert,
    row,
    reloading,
    setSelectedRow,
}: EnableOTPProps) => {
    const { t } = useTranslation();

    const [loading, setLoading] = useState(false);
    const [hasOtp, setHasOtp] = useState<boolean | null>(null);
    const [otpData, setOtpData] = useState<CreateUserOptResponse | null>(null);
    const [copied, setCopied] = useState(false);
    const [reloadOnClose, setReloadOnClose] = useState(false);
    const wasOpen = useRef(false);

    useEffect(() => {
        // Solo ejecutar cuando el diálogo pasa de cerrado a abierto
        if (open && !wasOpen.current && row) {
            wasOpen.current = true;
            setOtpData(null);
            setHasOtp(null);
            setReloadOnClose(false);

            const fetchUser = async () => {
                try {
                    setLoading(true);
                    const response = await getUserById(setLoading, row.id);
                    setHasOtp(!!response?.opt_secret);
                } catch (error) {
                    console.error("Error fetching user for OTP check:", error);
                    setHasOtp(false);
                } finally {
                    setLoading(false);
                }
            };

            fetchUser();
        }

        if (!open) {
            wasOpen.current = false;
        }
    }, [open, row]);

    const handleClose = () => {
        setOpen(false);
        setOtpData(null);
        setHasOtp(null);
        setCopied(false);
        if (reloadOnClose) {
            reloading();
        }
    };

    const handleCreate = async () => {
        try {
            const response = await createUserOpt(setLoading);
            if (response) {
                setOtpData(response);
                setHasOtp(true);
                setReloadOnClose(true);
            }
        } catch (error) {
            setAlert({ msg: t("user_management.table.dialog.otp.error_create") || "Error al activar OTP", severity: "error" });
            setOpenAlert(true);
        }
    };

    const handleDelete = async () => {
        try {
            await deleteUserOpt(setLoading);
            setHasOtp(false);
            setOtpData(null);
            setAlert({ msg: t("user_management.table.dialog.otp.deleted") || "OTP eliminado correctamente", severity: "success" });
            setOpenAlert(true);
            reloading();
            setSelectedRow([]);
            handleClose();
        } catch (error) {
            setAlert({ msg: t("user_management.table.dialog.otp.error_delete") || "Error al eliminar OTP", severity: "error" });
            setOpenAlert(true);
        }
    };

    const handleCopy = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const renderContent = () => {
        if (loading && hasOtp === null) {
            return (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (otpData) {
            return (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                        {t("user_management.table.dialog.otp.scan_instructions") || "Escanea el código QR con tu app de autenticación (Google Authenticator, Authy, etc.)"}
                    </Typography>

                    <Box
                        component="img"
                        src={`data:image/png;base64,${otpData.qr}`}
                        alt="OTP QR Code"
                        sx={{ width: 200, height: 200, border: "1px solid", borderColor: "divider", borderRadius: 1 }}
                    />

                    <Box sx={{ width: "100%" }}>
                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                            {t("user_management.table.dialog.otp.secret_key") || "Clave secreta (ingreso manual)"}
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                bgcolor: "action.hover",
                                borderRadius: 1,
                                px: 1.5,
                                py: 1,
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{ fontFamily: "monospace", flexGrow: 1, wordBreak: "break-all" }}
                            >
                                {otpData.secret_key}
                            </Typography>
                            <Tooltip title={copied ? (t("common.copied") || "¡Copiado!") : (t("common.copy") || "Copiar")}>
                                <IconButton size="small" onClick={() => handleCopy(otpData.secret_key)}>
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                </Box>
            );
        }

        if (hasOtp) {
            return (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Typography variant="body2">
                        {t("user_management.table.dialog.otp.already_enabled") || "El usuario ya tiene OTP activado."}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t("user_management.table.dialog.otp.delete_description") || "Si deseas desactivarlo, haz clic en Eliminar OTP."}
                    </Typography>
                </Box>
            );
        }

        return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography variant="body2">
                    {t("user_management.table.dialog.otp.not_enabled") || "El usuario no tiene OTP activado."}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t("user_management.table.dialog.otp.create_description") || "Haz clic en Activar OTP para generar un código QR y vincular tu app de autenticación."}
                </Typography>
            </Box>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : handleClose}
            fullWidth
            maxWidth="xs"
        >
            <DialogTitle>
                {t("user_management.table.dialog.otp.title") || "Autenticación OTP"}
            </DialogTitle>

            <IconButton
                aria-label="close"
                onClick={handleClose}
                disabled={loading}
                sx={{ position: "absolute", right: 8, top: 8 }}
            >
                <CloseIcon />
            </IconButton>

            <DialogContent dividers>
                {renderContent()}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    {t("common.close") || "Cerrar"}
                </Button>

                {!loading && hasOtp !== null && !otpData && (
                    hasOtp ? (
                        <LoadingButton
                            loading={loading}
                            variant="contained"
                            color="error"
                            onClick={handleDelete}
                        >
                            {t("user_management.table.dialog.otp.delete_button") || "Eliminar OTP"}
                        </LoadingButton>
                    ) : (
                        <LoadingButton
                            loading={loading}
                            variant="contained"
                            onClick={handleCreate}
                        >
                            {t("user_management.table.dialog.otp.create_button") || "Activar OTP"}
                        </LoadingButton>
                    )
                )}
            </DialogActions>
        </Dialog>
    );
};

export { EnableOTP };
